import { ref, computed } from 'vue'

/**
 * 登录态唯一真源 —— 取代原先 eventBus 的登录态广播。
 *
 * 为什么改：原实现是"发事件-等回调"（eventBus.emitLogin / onLogin / clearUser），
 * 导致 14 处 emitLogin、11 处 clearUser 散落各处，谁也不知道谁在监听；
 * 而且 offLogin 全项目零调用 ⇒ Layout 的监听会永久叠加（每进一次 Layout 多注册一份）。
 *
 * 为什么不用 Vuex：本项目虽装了 vuex 与 @vue/cli-plugin-vuex，
 * 但 src/store 目录不存在、main.js 未 app.use(store)、全项目零处使用
 * ⇒ 保持现状，用轻量模块单例，不在结构整改里引入新的状态管理方案。
 *
 * ⚠️ localStorage 键名 auth_token / is_admin 是全站契约（后端与 request.js 都认它），不可改。
 *
 * ★ V2-44-4 补的关键点（"登录状态不实时"的根因）：
 *   本模块的 token / isAdmin 是**内存 ref**，只在模块加载时从 localStorage 读一次。
 *   因此任何直接 localStorage.setItem('auth_token', ...) 的写法，
 *   内存里的 ref 都不会变 ⇒ Layout 的 watch 永不触发 ⇒ 导航栏不刷新。
 *   ⇒ **所有地方必须调 setToken / setIsAdmin**，不要裸写 localStorage。
 *   ⇒ 下面用 storage 事件解决"跨标签页"不同步。
 */

const TOKEN_KEY = 'auth_token'
const ADMIN_KEY = 'is_admin'

const token = ref(localStorage.getItem(TOKEN_KEY) || '')
const user = ref(null)
const isAdmin = ref(localStorage.getItem(ADMIN_KEY) === 'true')
const isLoggedIn = computed(() => !!token.value)

const setToken = (t) => {
    token.value = t || ''
    if (t) {
        localStorage.setItem(TOKEN_KEY, t)
    } else {
        localStorage.removeItem(TOKEN_KEY)
    }
}

const setUser = (u) => {
    user.value = u
}

/** 管理员标志（原先散落在 4 个文件里裸读写 localStorage） */
const setIsAdmin = (v) => {
    isAdmin.value = !!v
    if (v) {
        localStorage.setItem(ADMIN_KEY, 'true')
    } else {
        localStorage.removeItem(ADMIN_KEY)
    }
}

const clear = () => {
    user.value = null
    setToken('')
    setIsAdmin(false)
}

// 跨标签页同步：在 A 标签页登录/退出后，B 标签页也会跟着变。
// storage 事件只在**其它标签页**触发，本标签页 setToken 已直接改了内存值，不会重复触发。
window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEY) {
        token.value = localStorage.getItem(TOKEN_KEY) || ''
        if (!token.value) {
            user.value = null
            isAdmin.value = false
        }
    }
    if (e.key === ADMIN_KEY) {
        isAdmin.value = localStorage.getItem(ADMIN_KEY) === 'true'
    }
})

export function useAuthState() {
    return { token, user, isAdmin, isLoggedIn, setToken, setUser, setIsAdmin, clear }
}

/** 供 request.js 这类非组件环境直接使用 */
export const authState = { token, user, isAdmin, isLoggedIn, setToken, setUser, setIsAdmin, clear }